// Public legal root shell (FRONT-216). NO SPALayout / AuthGuard / org-slug / session — legal pages
// are fully public. Provides the standalone i18n provider (D9) + compass Tooltip/Toaster, plus the
// MINIMAL, NON-GATING render context that @crehana/web chrome (MainMenu/MainFooter) needs:
//   - `@crehana/ui` `Layout` = the styled-components ThemeProvider carrying the `@crehana/ui` theme
//     (so `theme.breakpoints.up()` exists for Col/Row/Container). Mirrors the source app's
//     `CrehanaUILayout` (`<Layout>` from @crehana/ui) in crehana-frontend `pages/_app.tsx`.
//   - `PublicMeContextProvider` = an ANONYMOUS, logged-out `useMe`/`useAuth` context so MainMenu/
//     MainFooter render in their public state. It NEVER fetches a session and NEVER redirects.
// This reproduces what anonymous visitors get on crehana.com — context only, never a login gate.
import '@/styles/global.css';
import { Toaster } from '@crehana/compass.ui/v3/toaster';
import { TooltipProvider } from '@crehana/compass.ui/v3/tooltip';
import { I18nProvider } from '@crehana/i18n';
import { type CountryCode, LANGUAGES } from '@crehana/ts-types';
// biome-ignore lint/style/noRestrictedImports: 1:1 migration needs @crehana/ui `Layout` (styled-components ThemeProvider) for @crehana/web/@crehana/ui chrome to render (FRONT-216)
import { Layout as CrehanaUIThemeLayout } from '@crehana/ui';
import { Outlet } from '@/shared/modern-router';
import PublicChromeContextProvider from '@/shared/public-chrome-context/public-chrome-context';
import { legalLocales } from '@/views/legal/shared/i18n';

/**
 * Expose the microfrontend name to the window object to make it easy to find the
 * microfrontend of the current page. Normally done by @crehana/layouts' SPA layout,
 * which this public shell intentionally does not use (see header comment).
 */
if (typeof window !== 'undefined') {
  window.CREHANA_MICROFRONTEND_NAME = process.env.CREHANA_MICROFRONTEND_NAME;
}

const SUPPORTED_COUNTRY_CODES: CountryCode[] = [
  'pe',
  'ar',
  'mx',
  'co',
  'es',
  'cl',
  'bo',
  'cr',
  'ec',
  'br',
];

// Detect UI language client-side from navigator.language; default ES. (No SSP language detection.)
function detectLang(): LANGUAGES {
  if (typeof navigator === 'undefined') {
    return LANGUAGES.ES;
  }
  const tag = navigator.language.toLowerCase();
  if (tag.startsWith('pt')) {
    return LANGUAGES.PT;
  }
  if (tag.startsWith('en')) {
    return LANGUAGES.EN;
  }
  return LANGUAGES.ES;
}

// Detect a sane country code from the navigator region; default PE.
function detectCountry(): CountryCode {
  if (typeof navigator === 'undefined') {
    return 'pe';
  }
  const region = (navigator.language.split('-')[1] ?? '').toLowerCase();
  const match = SUPPORTED_COUNTRY_CODES.find(code => code === region);
  return match ?? 'pe';
}

export default function RootLayout() {
  const languageCode = detectLang();
  const countryCode = detectCountry();

  return (
    <CrehanaUIThemeLayout>
      <PublicChromeContextProvider countryCode={countryCode}>
        <I18nProvider
          languageCode={languageCode}
          countryCode={countryCode}
          defaultTranslations={legalLocales}
        >
          <TooltipProvider>
            <Outlet />
            <Toaster />
          </TooltipProvider>
        </I18nProvider>
      </PublicChromeContextProvider>
    </CrehanaUIThemeLayout>
  );
}
